import {Card} from 'antd';
import React, {
    FC, useEffect, useState,
} from 'react';
import {useDispatch} from 'react-redux';

import {
    IDynamicTitle, IEntityMeta, IFormEntityMeta, loadMetadata, resetMetadata, selectMetadata, IUrlConfig,
} from 'entities/metadata';
import {selectContextRawData} from 'modules/context/context-selector';
import {selectEntityData, loadFormData, TEntity} from 'modules/data';
import {
    filterNonRenderableFieldsAndUpdateState as filterNonRenderableFieldsAndUpdateStateAction,
    initBlankForm as initBlankFormAction,
    performActionForForm,
    resetData,
    resetFormData,
    resetFormUsedAdditionalOptions,
} from 'modules/data/data-actions';
import {IFilterEntityData, IFormEntityData} from 'modules/data/data-types';
import {EEntityType} from 'shared/constants/entities';
import {TStateSetter} from 'shared/types/generics';
import {ERequestType} from 'shared/types/requests';
import {isEmptyObject} from 'shared/utils';
import {useAppSelector} from 'store/config/hooks';
import {selectBreadcrumbsData} from 'store/slices/breadcrumbs-slice';
import {selectModalData} from 'store/slices/modals-slice';

import {EFormMode} from '../form';
import {Form as FormComponent} from '../form/form';
import {Spinner} from '../spinner';

interface IFormContainerProps {
    entityName: string;
    url?: string;
    hideTitle?: boolean;
    match?: {
        params: { id: string };
    };
    onClose: () => void;
    dirty?: boolean;
    setIsDirty?: (v: boolean) => void;
    mode?: EFormMode;
    contextData?: Record<string, any>;
    additionalValuesForRequest?: Record<string, any>;
}

export const FormContainer: FC<IFormContainerProps> = ({
    entityName,
    url,
    match,
    ...props
}: IFormContainerProps) => {
    const [fieldsFiltered, setFieldsFiltered] = useState<boolean>(false);
    const dispatch = useDispatch();
    const contextData = useAppSelector(selectContextRawData);
    const metadata = useAppSelector(selectMetadata(entityName, EEntityType.FORM)) as IFormEntityMeta;
    const filter = useAppSelector(selectEntityData(entityName, EEntityType.FILTER)) as IFilterEntityData;
    const formData = useAppSelector(selectEntityData(entityName, EEntityType.FORM)) as IFormEntityData;
    const breadcrumbs = useAppSelector(selectBreadcrumbsData(entityName));
    const parentEntityName = useAppSelector(state => selectModalData(state)?.parentEntityName) || '';
    const modalParameters = useAppSelector(state => selectModalData(state)?.modalParameters);

    const filterNonRenderableFieldsAndUpdateState = (
        meta: IEntityMeta,
        name: string,
        entityType: EEntityType,
        setFilteredFlag: TStateSetter<boolean>,
    ) => dispatch(filterNonRenderableFieldsAndUpdateStateAction(meta, name, entityType, setFilteredFlag));

    useEffect(() => {
        if (match || modalParameters) {
            const id = match?.params?.id || modalParameters?.id;
            dispatch(loadMetadata(entityName, EEntityType.FORM));

            if (!formData || isEmptyObject(formData)) {
                let params = {};
                if (breadcrumbs) {
                    breadcrumbs.forEach(crumbItem => {
                        if (crumbItem?.children) {
                            crumbItem.children.forEach(childrenItem => {
                                params = {...params, [childrenItem.key]: childrenItem?.params};
                            });
                        }
                    });
                }
                dispatch(loadFormData(entityName, id, modalParameters?.referenceUrl ?? url, params, metadata?.urlConfig));
            }
        } else {
            dispatch(initBlankFormAction(entityName));
        }
        return () => {
            dispatch(resetFormData({entityName}));
            dispatch(resetFormUsedAdditionalOptions({entityName}));
            dispatch(resetData({entityName, entityType: EEntityType.NUMBER}));
            dispatch(resetMetadata({loading: false, entityName, entityType: EEntityType.FORM}));
        };
    }, []);

    const actionForForm = (
        name: string,
        referenceUrl: string,
        requestType: ERequestType,
        newData: Record<string, any>,
        isJsonRequest?: boolean,
        urlParamKey?: string,
        additionalData?: TEntity,
        urlConfig?: IUrlConfig,
    ) => dispatch(performActionForForm()(
        name, referenceUrl, requestType, newData, isJsonRequest, urlParamKey, additionalData, urlConfig,
    ));

    useEffect(() => {
        if (metadata?.fields && !fieldsFiltered) {
            filterNonRenderableFieldsAndUpdateState(metadata, metadata.name, EEntityType.FORM, setFieldsFiltered);
        }
    }, [metadata?.fields]);

    const editMode = !!match;
    const isLoaded = editMode ? !!metadata && !!formData && fieldsFiltered : !!metadata && fieldsFiltered;

    const dynamicTitleOptions: IDynamicTitle = metadata?.dynamicFormTitle as IDynamicTitle;

    const cardTitle = dynamicTitleOptions?.create;

    return isLoaded ? (
        <FormComponent
            cardTitle={cardTitle}
            parentEntityName={parentEntityName || entityName}
            tableFilterData={filter?.data}
            formData={formData}
            entityName={entityName}
            meta={metadata}
            actionForForm={actionForForm}
            contextData={contextData}
            {...props}
        />
    ) : (
        <Card style={{paddingTop: 20}}> <Spinner /></Card>
    );
};
